import React, { useEffect, useState } from "react"
import {
  Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, FormFeedback,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  getPushNotifications,
  getPushNotificationPreview,
  sendPushNotification,
  resendPushNotification,
  deletePushNotification,
} from "../../api/requests/push-notification-requests"
import { SubscriptionDeleteModal } from "../modals/subscription/SubscriptionDeleteModal"
import AnnouncementTabs from "./AnnouncementTabs"
import PaginationBar from "../../components/Common/PaginationBar"

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const HISTORY_REFRESH_MS = 10000
const PAGE_SIZE = 5

const targetScopeOptions = [
  { value: "ALL", label: "Herkes" },
  { value: "COMPANY_USERS", label: "Firma kullanıcıları" },
  { value: "ACCOUNT_USERS", label: "Cari/bayi kullanıcıları" },
]

const scopeLabel = (v) => targetScopeOptions.find((o) => o.value === v)?.label || v

const formatDateTime = (v) => {
  if (!v) return "—"
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })
}

const formatCount = (n) => Number(n || 0).toLocaleString("tr-TR")

const Card = ({ children, className = "" }) => (
  <div className={`card rounded-4 border-0 shadow-lg ${className}`} style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}>
    {children}
  </div>
)

const PushNotifications = () => {
  const [history, setHistory] = useState([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [detail, setDetail] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [pending, setPending] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [sending, setSending] = useState(false)

  document.title = "Push Bildirimleri | Tahsilat360"

  const styles = `
    .push-modern .table thead tr { background-color: #f9f9f9; }
    .push-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .push-modern .table td { vertical-align: middle; }
    .push-modern .btn-rounded { border-radius: 999px; }
    .push-modern .btn-navy, .push-confirm .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .push-modern .push-cell { max-width: 480px; }
    .push-modern .push-line { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .push-modern .push-row { cursor: pointer; }
    .push-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .push-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .push-modern .corporate-pagination .page-item.active .page-link {
      background-color: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .push-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .push-detail .detail-label { color: #6c757d; font-size: .8rem; margin-bottom: .1rem; }
    .push-detail .detail-value { font-size: .9rem; color: #212529; }
    .push-phone {
      background: #f2f3f7; border-radius: 18px; padding: .9rem;
      max-width: 340px; margin: 0 auto;
    }
    .push-phone .push-card {
      background: #fff; border-radius: 14px; padding: .65rem .75rem;
      box-shadow: 0 2px 8px rgba(0,0,0,.08);
      display: flex; gap: .6rem; align-items: flex-start;
    }
    .push-phone .push-app-icon {
      width: 30px; height: 30px; border-radius: 8px; background: ${NAVY};
      color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .push-phone .push-title { font-weight: 600; font-size: .85rem; color: #111; word-break: break-word; }
    .push-phone .push-body {
      font-size: .8rem; color: #444; word-break: break-word;
      display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;
    }
    @media (max-width: 576px) {
      .push-modern .push-cell { max-width: 100%; }
      .push-modern .table { font-size: 12px; }
      .push-modern .table td, .push-modern .table th { padding: .5rem; }
    }
  `

  const loadHistory = async (p = page) => {
    try {
      setError(null)
      const res = await getPushNotifications(p, PAGE_SIZE)
      const data = res.data || {}
      setHistory(data.content || [])
      setTotalPages(data.totalPages || 0)
      setTotalElements(data.totalElements || 0)
      setPage(data.page ?? p)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadHistory(0)
  }, [])

  const hasPending = history.some((h) => h.pendingCount > 0)
  useEffect(() => {
    if (!hasPending) return undefined
    const timer = setInterval(() => loadHistory(page), HISTORY_REFRESH_MS)
    return () => clearInterval(timer)
  }, [hasPending, page])

  const openConfirm = async (payload) => {
    setPending(payload)
    setPreview(null)
    setPreviewLoading(true)
    setConfirmOpen(true)
    try {
      const res = await getPushNotificationPreview(payload.targetScope)
      setPreview(res.data)
    } catch (err) {
      toast.error(err.message)
      setConfirmOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const openResend = (h) => {
    setDetail(null)
    openConfirm({
      title: h.title, body: h.body, targetScope: h.targetScope, addAsAnnouncement: false,
      resendId: h.id, nextSendNo: (h.sendCount || 1) + 1,
    })
  }

  const formik = useFormik({
    initialValues: { title: "", body: "", targetScope: "ALL", addAsAnnouncement: false },
    validationSchema: Yup.object({
      title: Yup.string().trim().max(100, "Başlık en fazla 100 karakter olabilir").required("Başlık gerekli"),
      body: Yup.string().trim().max(500, "Metin en fazla 500 karakter olabilir").required("Bildirim metni gerekli"),
      targetScope: Yup.string().oneOf(["ALL", "COMPANY_USERS", "ACCOUNT_USERS"]).required("Hedef kitle gerekli"),
    }),
    onSubmit: (values) => openConfirm({ ...values, resendId: null }),
  })

  const handleSend = async () => {
    setSending(true)
    try {
      const res = pending.resendId
        ? await resendPushNotification(pending.resendId)
        : await sendPushNotification(pending)
      toast.success(res.message || "Push bildirimi gönderimi başlatıldı")
      setConfirmOpen(false)
      if (!pending.resendId) formik.resetForm()
      loadHistory(0)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setSending(false)
    }
  }

  const openDelete = (h) => {
    setDetail(null)
    setDeleteTarget(h)
  }

  const handleDelete = async (id) => {
    try {
      const res = await deletePushNotification(id)
      toast.success(res.message || "Push bildirimi silindi")
      // Sayfadaki son kayıt silindiyse bir önceki sayfaya dön.
      loadHistory(history.length === 1 && page > 0 ? page - 1 : page)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeleteTarget(null)
    }
  }

  const err = (field) => formik.touched[field] && formik.errors[field]
  const noRecipient = preview && preview.recipientCount === 0

  return (
    <div className="page-content push-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <AnnouncementTabs />

        <Row>
          <Col lg={12}>
            <Card className="mb-4">
              <CardHeader className="bg-white border-0 rounded-top-4">
                <h5 className="mb-1 fw-bold text-dark">Yeni Push Bildirimi</h5>
                <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                  Bildirim, mobil uygulamada oturum açmış kullanıcıların cihazlarına gider.
                </p>
              </CardHeader>
              <CardBody className="pt-2">
                <Row>
                  <Col md={7}>
                    <FormGroup>
                      <Label>Başlık <span className="text-danger">*</span></Label>
                      <Input name="title" value={formik.values.title} onChange={formik.handleChange}
                        onBlur={formik.handleBlur} invalid={!!err("title")} maxLength={100}
                        placeholder="Yeni özellik kullanıma açıldı" />
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                      <div className="text-muted small mt-1">{formik.values.title.length}/100</div>
                    </FormGroup>
                    <FormGroup>
                      <Label>Bildirim Metni <span className="text-danger">*</span></Label>
                      <Input type="textarea" rows={4} name="body" value={formik.values.body}
                        onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={!!err("body")}
                        maxLength={500} placeholder="Tahsilatlarınızı artık yeni raporlar ekranından takip edebilirsiniz." />
                      <FormFeedback>{formik.errors.body}</FormFeedback>
                      <div className="text-muted small mt-1">{formik.values.body.length}/500</div>
                    </FormGroup>
                    <FormGroup>
                      <Label>Hedef Kitle <span className="text-danger">*</span></Label>
                      <Select
                        options={targetScopeOptions}
                        value={targetScopeOptions.find((o) => o.value === formik.values.targetScope) || null}
                        onChange={(o) => formik.setFieldValue("targetScope", o?.value)}
                        placeholder="Seçiniz"
                      />
                    </FormGroup>
                    <FormGroup check className="mt-3">
                      <Input type="checkbox" id="push-add-announcement" name="addAsAnnouncement"
                        checked={formik.values.addAsAnnouncement} onChange={formik.handleChange} />
                      <Label check for="push-add-announcement" className="ms-1">Duyuru olarak da ekle</Label>
                      <div className="text-muted small">
                        Aynı başlık ve metinle, aynı hedef kitleye yayında bir duyuru oluşturulur.
                        Mobilde bildirime dokunan kullanıcı bu duyuruya yönlendirilir.
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={5} className="mt-3 mt-md-0">
                    <Label>Önizleme</Label>
                    <div className="push-phone">
                      <div className="push-card">
                        <div className="push-app-icon"><i className="bx bx-bell" /></div>
                        <div style={{ minWidth: 0 }}>
                          <div className="push-title">{formik.values.title || "Başlık"}</div>
                          <div className="push-body">{formik.values.body || "Bildirim metni"}</div>
                        </div>
                      </div>
                    </div>
                    <div className="text-muted small mt-2 text-center">
                      Görünüm cihaza göre değişir; uzun metinler bildirimde kısaltılır.
                    </div>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-2">
                  <Button className="btn-rounded btn-navy d-flex align-items-center gap-1" onClick={formik.handleSubmit}>
                    <i className="bx bx-send" /> Gönder
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="bg-white border-0 rounded-top-4 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Gönderim Geçmişi</h5>
                  <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>Ayrıntı için satıra tıklayın</p>
                </div>
                <button className="btn btn-light btn-sm btn-rounded" onClick={() => loadHistory(page)} title="Yenile">
                  <i className="bx bx-refresh" />
                </button>
              </CardHeader>
              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />{error}
                  </Alert>
                )}
                {loading ? (
                  <div className="text-center p-5"><Spinner color="primary" /></div>
                ) : (
                  <>
                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Son Gönderim</Th>
                            <Th>Bildirim</Th>
                            <Th className="d-none d-lg-table-cell">Hedef</Th>
                            <Th className="text-center">Gönderim</Th>
                            <Th>Alıcı</Th>
                            <Th>Durum</Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {history.length ? history.map((h) => (
                            <Tr key={h.id} className="border-bottom push-row" onClick={() => setDetail(h)}>
                              <Td className="small text-nowrap">{formatDateTime(h.lastSentAt || h.createdAt)}</Td>
                              <Td>
                                <div className="push-cell">
                                  <div className="fw-semibold text-dark push-line">{h.title}</div>
                                  <div className="text-muted small push-line">{h.body}</div>
                                  {h.announcementId && (
                                    <span className="badge border-0 mt-1" style={{ borderRadius: 999, backgroundColor: `${NAVY}18`, color: NAVY }}>
                                      <i className="bx bx-broadcast me-1" />Duyuruya bağlı
                                    </span>
                                  )}
                                </div>
                              </Td>
                              <Td className="d-none d-lg-table-cell small">{scopeLabel(h.targetScope)}</Td>
                              <Td className="text-center">
                                <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: `${NAVY}18`, color: NAVY }}>
                                  {formatCount(h.sendCount || 1)} kez
                                </span>
                              </Td>
                              <Td className="small text-nowrap">
                                {formatCount(h.recipientCount)} kullanıcı
                                <div className="text-muted">{formatCount(h.deviceCount)} cihaz</div>
                              </Td>
                              <Td className="small text-nowrap">
                                <div style={{ color: "#71C30B" }}>{formatCount(h.sentCount)} iletildi</div>
                                {h.pendingCount > 0 && <div className="text-muted">{formatCount(h.pendingCount)} sırada</div>}
                                {h.failedCount > 0 && <div className="text-danger">{formatCount(h.failedCount)} başarısız</div>}
                              </Td>
                              <Td className="text-end">
                                <div className="d-flex gap-1 justify-content-end">
                                  <button className="btn btn-sm action-btn btn-navy" title="Tekrar gönder"
                                    onClick={(e) => { e.stopPropagation(); openResend(h) }}>
                                    <i className="bx bx-redo" />
                                  </button>
                                  <button className="btn btn-sm action-btn"
                                    style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }}
                                    title="Sil" onClick={(e) => { e.stopPropagation(); openDelete(h) }}>
                                    <i className="bx bx-trash" />
                                  </button>
                                </div>
                              </Td>
                            </Tr>
                          )) : (
                            <Tr>
                              <Td colSpan={7} className="text-center py-5">
                                <i className="bx bx-mobile-alt text-muted" style={{ fontSize: "2.5rem" }} />
                                <h6 className="mt-3 text-muted">Henüz push bildirimi gönderilmedi</h6>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>
                    <PaginationBar
                      currentPage={page}
                      totalPages={totalPages}
                      totalElements={totalElements}
                      pageSize={PAGE_SIZE}
                      onPageChange={(p) => loadHistory(p)}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal isOpen={!!detail} toggle={() => setDetail(null)} centered size="lg" className="push-detail push-confirm">
        <ModalHeader toggle={() => setDetail(null)}>
          <i className="bx bx-mobile-alt me-2" style={{ color: NAVY }} />Push Bildirimi Ayrıntısı
        </ModalHeader>
        {detail && (
          <ModalBody>
            <h6 className="fw-bold text-dark mb-2" style={{ wordBreak: "break-word" }}>{detail.title}</h6>
            <p className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{detail.body}</p>
            <Row className="g-3">
              <Col xs={6} md={4}>
                <div className="detail-label">Son gönderim</div>
                <div className="detail-value">{formatDateTime(detail.lastSentAt || detail.createdAt)}</div>
              </Col>
              <Col xs={6} md={4}>
                <div className="detail-label">İlk gönderim</div>
                <div className="detail-value">{formatDateTime(detail.createdAt)}</div>
              </Col>
              <Col xs={6} md={4}>
                <div className="detail-label">Gönderim sayısı</div>
                <div className="detail-value">{formatCount(detail.sendCount || 1)} kez</div>
              </Col>
              <Col xs={6} md={4}>
                <div className="detail-label">Gönderen</div>
                <div className="detail-value text-break">{detail.createdByEmail || "—"}</div>
              </Col>
              <Col xs={6} md={4}>
                <div className="detail-label">Hedef kitle</div>
                <div className="detail-value">{scopeLabel(detail.targetScope)}</div>
              </Col>
              <Col xs={6} md={4}>
                <div className="detail-label">Alıcı (son gönderim)</div>
                <div className="detail-value">
                  {formatCount(detail.recipientCount)} kullanıcı · {formatCount(detail.deviceCount)} cihaz
                </div>
              </Col>
              <Col xs={12} md={8}>
                <div className="detail-label">Son gönderimin durumu (kullanıcı bazında)</div>
                <div className="detail-value d-flex flex-wrap gap-3">
                  <span style={{ color: "#71C30B" }}>{formatCount(detail.sentCount)} iletildi</span>
                  <span className="text-muted">{formatCount(detail.pendingCount)} sırada</span>
                  <span className="text-danger">{formatCount(detail.failedCount)} başarısız</span>
                </div>
              </Col>
              <Col xs={12}>
                <div className="detail-label">Duyuru</div>
                <div className="detail-value">
                  {detail.announcementId ? "Bu bildirim bir duyuruya bağlı; mobilde dokunulunca duyuru açılır." : "Duyuruya bağlı değil."}
                </div>
              </Col>
            </Row>
          </ModalBody>
        )}
        <ModalFooter>
          <Button color="danger" outline className="btn-rounded me-auto d-flex align-items-center gap-1" onClick={() => openDelete(detail)}>
            <i className="bx bx-trash" /> Sil
          </Button>
          <Button color="secondary" className="btn-rounded" onClick={() => setDetail(null)}>Kapat</Button>
          <Button className="btn-rounded btn-navy d-flex align-items-center gap-1" onClick={() => openResend(detail)}>
            <i className="bx bx-redo" /> Tekrar Gönder
          </Button>
        </ModalFooter>
      </Modal>

      <SubscriptionDeleteModal
        isOpen={!!deleteTarget}
        toggle={() => setDeleteTarget(null)}
        title="Push Bildirimi Silme Onayı"
        icon="bx-mobile-alt"
        message="Bu bildirim gönderim geçmişinden kalıcı olarak silinecek. Telefonlara ulaşmış bildirimler geri alınamaz; sırada bekleyen gönderimler iptal edilir. Bağlı duyuru silinmez."
        item={deleteTarget}
        renderInfo={(h) => (
          <div style={{ minWidth: 0 }}>
            <h6 className="mb-1 text-dark text-truncate">{h.title}</h6>
            <div className="text-muted small text-truncate">{h.body}</div>
            <div className="text-muted small mt-1">
              {formatCount(h.sendCount || 1)} kez gönderildi · son {formatDateTime(h.lastSentAt || h.createdAt)}
            </div>
          </div>
        )}
        onConfirm={handleDelete}
      />

      <Modal isOpen={confirmOpen} toggle={() => !sending && setConfirmOpen(false)} centered className="push-confirm">
        <ModalHeader toggle={() => !sending && setConfirmOpen(false)}>
          <i className={`bx ${pending?.resendId ? "bx-redo" : "bx-send"} me-2`} style={{ color: NAVY }} />
          {pending?.resendId ? "Push Bildirimini Tekrar Gönder" : "Push Bildirimi Gönder"}
        </ModalHeader>
        <ModalBody>
          {previewLoading ? (
            <div className="text-center p-3"><Spinner size="sm" className="me-2" />Alıcılar hesaplanıyor...</div>
          ) : preview && pending && (
            <>
              <div className="fw-semibold text-dark">{pending.title}</div>
              <div className="text-muted small mb-3" style={{ whiteSpace: "pre-wrap" }}>{pending.body}</div>
              {noRecipient ? (
                <Alert color="warning" className="rounded-4 mb-0">
                  Seçilen hedefte mobil uygulaması kayıtlı kullanıcı yok.
                </Alert>
              ) : (
                <p className="mb-0">
                  <strong>{scopeLabel(pending.targetScope)}</strong> hedefindeki{" "}
                  <strong>{formatCount(preview.recipientCount)} kullanıcıya</strong>{" "}
                  ({formatCount(preview.deviceCount)} cihaz) gönderilecek.
                  {pending.addAsAnnouncement && " Aynı metinle bir duyuru da yayına alınacak."}
                  {pending.resendId && ` Bu, bildirimin ${pending.nextSendNo}. gönderimi olacak; geçmişte aynı satır güncellenir. Alıcılar şu anki cihaz kayıtlarına göre hesaplandı.`}
                  <span className="d-block text-muted small mt-2">Gönderilen bildirim geri alınamaz.</span>
                </p>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" className="btn-rounded" onClick={() => setConfirmOpen(false)} disabled={sending}>
            Vazgeç
          </Button>
          <Button className="btn-rounded btn-navy" onClick={handleSend} disabled={sending || previewLoading || !preview || noRecipient}>
            {sending && <Spinner size="sm" className="me-2" />}Gönder
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default PushNotifications
